/**
 * Service de gestion du cache local (localStorage)
 * Pour stocker temporairement les données utilisateur non authentifié
 */

import logger from './logger';

const CACHE_KEYS = {
  USER_DATA: 'mirror_user_data',
  ANONYMOUS_ID: 'mirror_anonymous_id',
};

/**
 * Génère un UUID v4 pour identifier un utilisateur anonyme de manière persistante
 * Cet ID reste le même tant que le localStorage n'est pas vidé
 */
const generateUUID = () => {
  // Utilise crypto.randomUUID si disponible (navigateurs modernes)
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback pour les navigateurs plus anciens
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

/**
 * Sauvegarde les données de profil utilisateur dans le cache local
 * Seules les données personnelles sont sauvegardées (pas les données produit/gateway)
 * @param {Object} userData - Données utilisateur complètes
 */
export const saveUserDataToCache = (userData) => {
  try {
    const anonymousId = localStorage.getItem(CACHE_KEYS.ANONYMOUS_ID) || generateUUID();
    localStorage.setItem(CACHE_KEYS.ANONYMOUS_ID, anonymousId);

    const dataToSave = {
      gender: userData.gender || '',
      height: userData.height || '',
      weight: userData.weight || '',
      age: userData.age || '',
      deltaChest: userData.deltaChest ?? 0,
      deltaWaist: userData.deltaWaist ?? 0,
      deltaHips: userData.deltaHips ?? 0,
      manualChest: userData.manualChest ?? null,
      manualWaist: userData.manualWaist ?? null,
      manualHips: userData.manualHips ?? null,
      selfieBase64: userData.selfieBase64 || '',
      avatarBase64: userData.avatarBase64 || '',
      anonymousId,
      savedAt: new Date().toISOString(),
    };

    localStorage.setItem(CACHE_KEYS.USER_DATA, JSON.stringify(dataToSave));
    logger.log('Données sauvegardées dans le cache');
    return true;
  } catch (error) {
    logger.error('Erreur lors de la sauvegarde dans le cache:', error);
    return false;
  }
};

/**
 * Récupère les données utilisateur depuis le cache local
 * @returns {Object|null} Données utilisateur ou null
 */
export const getUserDataFromCache = () => {
  try {
    const cachedData = localStorage.getItem(CACHE_KEYS.USER_DATA)
      || localStorage.getItem('younzee_user_data');
    if (!cachedData) {
      return null;
    }

    const userData = JSON.parse(cachedData);
    // Migre les données sous la nouvelle clé si elles venaient de l'ancienne
    if (!localStorage.getItem(CACHE_KEYS.USER_DATA)) {
      localStorage.setItem(CACHE_KEYS.USER_DATA, cachedData);
    }
    return userData;
  } catch (error) {
    logger.error('Erreur lors de la récupération du cache:', error);
    return null;
  }
};

/**
 * Vérifie si des données en cache existent
 * @returns {boolean}
 */
export const hasCachedData = () => {
  return !!localStorage.getItem(CACHE_KEYS.USER_DATA);
};

/**
 * Supprime les données utilisateur du cache
 */
export const clearUserCache = () => {
  try {
    localStorage.removeItem(CACHE_KEYS.USER_DATA);
    logger.log('Cache supprimé');
    return true;
  } catch (error) {
    logger.error('Erreur lors de la suppression du cache:', error);
    return false;
  }
};

/**
 * Supprime tout le cache Younzee
 */
export const clearAllCache = () => {
  try {
    Object.values(CACHE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
    logger.log('Tout le cache a été supprimé');
    return true;
  } catch (error) {
    logger.error('Erreur lors de la suppression complète du cache:', error);
    return false;
  }
};

const compressImageTo1080p = (base64Image) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const maxHeight = 1080;
      const scale = img.height > maxHeight ? maxHeight / img.height : 1;
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.85));
    };
    img.onerror = () => resolve(base64Image);
    img.src = base64Image.startsWith('data:')
      ? base64Image
      : `data:image/png;base64,${base64Image}`;
  });

/**
 * Compresse et sauvegarde l'image avatar VTO dans le cache, supprime le selfie.
 * La compression en JPEG 1080p réduit une image 2K PNG ~4MB à ~400KB-1.2MB.
 * @param {string} avatarBase64 - Image VTO résultat en base64 (raw ou data URI)
 */
export const saveAvatarToCache = async (avatarBase64) => {
  try {
    const compressed = await compressImageTo1080p(avatarBase64);
    const existingData = getUserDataFromCache() || {};
    const updatedData = {
      ...existingData,
      avatarBase64: compressed,
      selfieBase64: '',
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(CACHE_KEYS.USER_DATA, JSON.stringify(updatedData));
    logger.log('Avatar VTO compressé et sauvegardé dans le cache');
    return true;
  } catch (error) {
    logger.error('Erreur lors de la sauvegarde de l\'avatar dans le cache:', error);
    return false;
  }
};

/**
 * Met à jour partiellement les données en cache
 * @param {Object} updates - Données à mettre à jour
 */
export const updateCachedUserData = (updates) => {
  try {
    const existingData = getUserDataFromCache() || {};
    const updatedData = {
      ...existingData,
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(CACHE_KEYS.USER_DATA, JSON.stringify(updatedData));
    return true;
  } catch (error) {
    logger.error('Erreur lors de la mise à jour du cache:', error);
    return false;
  }
};

/**
 * Récupère l'ID anonyme de l'utilisateur (persistant)
 * Génère un nouvel UUID si aucun n'existe
 * @returns {string}
 */
export const getAnonymousId = () => {
  let anonymousId = localStorage.getItem(CACHE_KEYS.ANONYMOUS_ID);
  if (!anonymousId) {
    anonymousId = localStorage.getItem('younzee_anonymous_id') || generateUUID();
    localStorage.setItem(CACHE_KEYS.ANONYMOUS_ID, anonymousId);
  }
  return anonymousId;
};
